/**
 * API client for Cazino backend
 */

import type {
  Market,
  User,
  Bet,
  LeaderboardEntry,
  FeedItem,
  ApiError,
} from "./types";

const API_BASE = "/api";

class ApiClient {
  private async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      throw new Error(error.error?.message ?? "Request failed");
    }

    return data as T;
  }

  // Markets
  async createMarket(params: {
    name: string;
    adminName: string;
    startingBalance?: number;
    deviceId: string;
    inviteCode?: string;
  }): Promise<{ market: Market; user: User; inviteCode: string }> {
    return this.request("/markets", {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  async joinMarket(
    inviteCode: string,
    params: { displayName: string; avatar?: string; deviceId: string },
  ): Promise<{ market: Market; user: User }> {
    return this.request(`/markets/${inviteCode}/join`, {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  async getMarket(marketId: string): Promise<Market> {
    return this.request(`/markets/${marketId}`);
  }

  async getLeaderboard(
    marketId: string,
  ): Promise<{ users: LeaderboardEntry[] }> {
    return this.request(`/markets/${marketId}/leaderboard`);
  }

  async getFeed(marketId: string): Promise<{ feed: FeedItem[] }> {
    return this.request(`/markets/${marketId}/feed`);
  }

  async openMarket(marketId: string, adminId: string): Promise<void> {
    await this.request(`/markets/${marketId}/open/${adminId}`, {
      method: "POST",
    });
  }

  async closeMarket(marketId: string, adminId: string): Promise<void> {
    await this.request(`/markets/${marketId}/close/${adminId}`, {
      method: "POST",
    });
  }

  // Bets
  async getBets(marketId: string, userId: string): Promise<Bet[]> {
    return this.request(`/markets/${marketId}/bets/${userId}`);
  }

  async createBet(
    marketId: string,
    creatorId: string,
    params: {
      subjectUserId: string;
      description: string;
      initialOdds?: string;
      openingWager: number;
      hideFromSubject?: boolean;
    },
  ): Promise<{ bet: Bet }> {
    return this.request(`/markets/${marketId}/bets/${creatorId}`, {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  async getPendingBets(marketId: string): Promise<Bet[]> {
    return this.request(`/markets/${marketId}/bets-pending`);
  }

  async approveBet(betId: string, adminId: string): Promise<void> {
    await this.request(`/${betId}/approve/${adminId}`, {
      method: "POST",
    });
  }

  async placeWager(
    betId: string,
    userId: string,
    params: { side: "YES" | "NO"; amount: number },
  ): Promise<{ newProbability: number }> {
    return this.request(`/${betId}/wager/${userId}`, {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  async resolveBet(
    betId: string,
    adminId: string,
    outcome: boolean,
  ): Promise<void> {
    await this.request(`/${betId}/resolve/${adminId}`, {
      method: "POST",
      body: JSON.stringify({ outcome }),
    });
  }

  async deleteBet(betId: string, userId: string): Promise<void> {
    await this.request(`/${betId}/delete/${userId}`, {
      method: "POST",
    });
  }

  // Users
  async getUser(userId: string): Promise<User> {
    return this.request(`/users/${userId}`);
  }

  // Devices
  async getDeviceMarkets(
    deviceId: string,
  ): Promise<{ markets: { market: Market; user: User }[] }> {
    return this.request(`/devices/${deviceId}/markets`);
  }
}

export const api = new ApiClient();
